import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../api';
import BackButton from '../components/BackButton';
import styles from './Auth.module.css';

// ─── LOGIN ─────────────────────────────────────────────────────
export function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || '/';

  const [form, setForm] = useState({ identifier: '', password: '', rememberMe: false });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const submit = async e => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await login(form.identifier, form.password, form.rememberMe);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      <BackButton />
      <div className={styles.card}>
        <div className={styles.header}>
          <h1 className={styles.title}>Welcome back.</h1>
          <p className={styles.sub}>Don't have an account? <Link to="/register" className={styles.link}>Sign up</Link></p>
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        <form onSubmit={submit} className={styles.form}>
          <div className="form-group">
            <label className="form-label">Email or username</label>
            <input className="form-input" type="text" value={form.identifier} onChange={set('identifier')} required autoFocus />
          </div>

          <div className="form-group">
            <label className="form-label">Password</label>
            <input className="form-input" type="password" value={form.password} onChange={set('password')} required />
          </div>

          <div className={styles.row}>
            <label className={styles.checkLabel}>
              <input type="checkbox" checked={form.rememberMe} onChange={set('rememberMe')} />
              Remember me
            </label>
            <Link to="/forgot-password" className={styles.link}>Forgot password?</Link>
          </div>

          <button type="submit" className="btn btn-magenta" style={{ width: '100%' }} disabled={loading}>
            {loading ? 'Logging in…' : 'Log In'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ─── REGISTER ─────────────────────────────────────────────────
export function Register() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: '', email: '', password: '', confirm: '', displayName: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    if (form.password !== form.confirm) return 'Passwords do not match';
    if (form.password.length < 8) return 'Password must be at least 8 characters';
    if (!/[A-Z]/.test(form.password)) return 'Password must contain an uppercase letter';
    if (!/\d/.test(form.password)) return 'Password must contain a number';
    return null;
  };

  const submit = async e => {
    e.preventDefault();
    const err = validate();
    if (err) { setError(err); return; }
    setError(''); setLoading(true);
    try {
      await api.register({ username: form.username, email: form.email, password: form.password, displayName: form.displayName });
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  if (success) return (
    <div className={styles.page}>
      <BackButton />
      <div className={styles.card}>
        <div className={styles.successIcon}>✉️</div>
        <h2 className={styles.title}>Check your inbox</h2>
        <p className={styles.sub}>We sent a verification link to <strong>{form.email}</strong>. Click it to activate your account.</p>
        <Link to="/login" className="btn btn-magenta" style={{ marginTop: 24, display: 'inline-flex' }}>Go to Login</Link>
      </div>
    </div>
  );

  return (
    <div className={styles.page}>
      <BackButton />
      <div className={styles.card}>
        <div className={styles.header}>
          <h1 className={styles.title}>Join the revolution.</h1>
          <p className={styles.sub}>Already have an account? <Link to="/login" className={styles.link}>Log in</Link></p>
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        <form onSubmit={submit} className={styles.form}>
          <div className="form-group">
            <label className="form-label">Display name (optional)</label>
            <input className="form-input" type="text" value={form.displayName} onChange={set('displayName')} placeholder="How you appear on the platform" />
          </div>
          <div className="form-group">
            <label className="form-label">Username *</label>
            <input className="form-input" type="text" value={form.username} onChange={set('username')} required pattern="[a-zA-Z0-9_]+" title="Letters, numbers, underscores only" />
          </div>
          <div className="form-group">
            <label className="form-label">Email *</label>
            <input className="form-input" type="email" value={form.email} onChange={set('email')} required />
          </div>
          <div className="form-group">
            <label className="form-label">Password *</label>
            <input className="form-input" type="password" value={form.password} onChange={set('password')} required />
            <span className="form-error" style={{ color: 'var(--gray)', fontSize: 12 }}>Min 8 chars, 1 uppercase, 1 number</span>
          </div>
          <div className="form-group">
            <label className="form-label">Confirm password *</label>
            <input className="form-input" type="password" value={form.confirm} onChange={set('confirm')} required />
          </div>

          <button type="submit" className="btn btn-magenta" style={{ width: '100%' }} disabled={loading}>
            {loading ? 'Creating account…' : 'Create Account'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ─── FORGOT PASSWORD ───────────────────────────────────────────
export function ForgotPassword() {
  const [emailVal, setEmailVal] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async e => {
    e.preventDefault();
    setLoading(true);
    try { await api.forgotPassword(emailVal); } catch (_) {}
    setSent(true);
    setLoading(false);
  };

  return (
    <div className={styles.page}>
      <BackButton />
      <div className={styles.card}>
        {sent ? (
          <>
            <div className={styles.successIcon}>📬</div>
            <h2 className={styles.title}>Check your email</h2>
            <p className={styles.sub}>If that address is registered, you'll receive a password reset link shortly.</p>
            <Link to="/login" className="btn btn-outline-magenta" style={{ marginTop: 24, display: 'inline-flex' }}>Back to Login</Link>
          </>
        ) : (
          <>
            <div className={styles.header}>
              <h1 className={styles.title}>Reset password</h1>
              <p className={styles.sub}>Enter your email and we'll send you a reset link.</p>
            </div>
            <form onSubmit={submit} className={styles.form}>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input className="form-input" type="email" value={emailVal} onChange={e => setEmailVal(e.target.value)} required autoFocus />
              </div>
              <button type="submit" className="btn btn-magenta" style={{ width: '100%' }} disabled={loading}>
                {loading ? 'Sending…' : 'Send Reset Link'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

// ─── RESET PASSWORD ────────────────────────────────────────────
export function ResetPassword() {
  const location = useLocation();
  const navigate = useNavigate();
  const token = new URLSearchParams(location.search).get('token');
  const [form, setForm] = useState({ password: '', confirm: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async e => {
    e.preventDefault();
    if (form.password !== form.confirm) { setError('Passwords do not match'); return; }
    setError(''); setLoading(true);
    try {
      await api.resetPassword({ token, password: form.password });
      navigate('/login', { state: { message: 'Password reset! Please log in.' } });
    } catch (err) {
      setError(err.message || 'Reset failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      <BackButton />
      <div className={styles.card}>
        <div className={styles.header}>
          <h1 className={styles.title}>New password</h1>
        </div>
        {error && <div className="alert alert-error">{error}</div>}
        <form onSubmit={submit} className={styles.form}>
          <div className="form-group">
            <label className="form-label">New password</label>
            <input className="form-input" type="password" value={form.password} onChange={set('password')} required />
          </div>
          <div className="form-group">
            <label className="form-label">Confirm password</label>
            <input className="form-input" type="password" value={form.confirm} onChange={set('confirm')} required />
          </div>
          <button type="submit" className="btn btn-magenta" style={{ width: '100%' }} disabled={loading}>
            {loading ? 'Resetting…' : 'Reset Password'}
          </button>
        </form>
      </div>
    </div>
  );
}
